import { useCallback, useRef } from 'react';
import { Button } from '../../components/Button';
import type { PublicPricingResponse } from '../../types/pricing';
import { MarketingFooter, MarketingHeader } from '../marketing/MarketingChrome';
import { PricingCta } from './PricingCta';
import { PricingFaq, PricingHowItWorks } from './PricingInfo';
import { PricingPlanCard } from './PricingPlanCard';
import { PricingTierCard } from './PricingTierCard';
import { usePublicPricing, type PublicPricingState } from './usePublicPricing';
import './PricingPage.css';

/** The trial line. Null when the API does not say how long the trial is (an older server). */
const trialLine = (trialDays: number | null): string | null =>
  trialDays === null ? null : `Start with a ${trialDays}-day free trial. No card needed.`;

/** Baseball plans are on sale (an API older than plans sends none, and the page reads as before). */
const hasPlans = (pricing: PublicPricingResponse): boolean => (pricing.planTiers ?? []).length > 0;

const TierCards = ({ pricing, headingLevel }: { pricing: PublicPricingResponse; headingLevel?: 3 | 4 }) => (
  <ul className="pricing-tiers" aria-label="Pricing tiers" data-testid="pricing-tiers">
    {pricing.tiers.map((tier) => (
      <li key={tier.key} className="pricing-tiers__item">
        <PricingTierCard
          tier={tier}
          currency={pricing.currency}
          signupAvailable={pricing.signupAvailable}
          trialDays={pricing.trialDays ?? null}
          headingLevel={headingLevel}
        />
      </li>
    ))}
  </ul>
);

const SPORTS = [
  { key: 'baseball', label: 'Baseball' },
  { key: 'softball', label: 'Softball' },
] as const;

/** The sports with plans on sale. */
const sportsWithPlans = (pricing: PublicPricingResponse): Set<string> =>
  new Set((pricing.planTiers ?? []).map((plan) => plan.sport));

/**
 * One section per sport: its plans when it has any on sale, else (softball, while it has no plans)
 * the school tier cards. Plan names, prices and what each includes all come from the API (admins
 * edit them); only the core app line is fixed copy.
 */
const SportSections = ({ pricing }: { pricing: PublicPricingResponse }) => {
  const withPlans = sportsWithPlans(pricing);
  return (
    <>
      {SPORTS.map(({ key, label }) => {
        const plans = (pricing.planTiers ?? []).filter((plan) => plan.sport === key);
        if (plans.length > 0) {
          return (
            <section key={key} className="pricing-sport" aria-labelledby={`pricing-sport-${key}`} data-testid={`pricing-${key}`}>
              <h3 className="pricing-sport__title" id={`pricing-sport-${key}`}>
                {label}
              </h3>
              <p className="pricing-sport__lead">
                Pick the plan that fits your program. Every plan includes the core app: recruit profiles,
                notes and video, your recruiting board, depth chart, call lists and scholarship tracking.
              </p>
              <ul className="pricing-tiers" aria-label={`${label} plans`} data-testid={`pricing-plans-${key}`}>
                {plans.map((plan) => (
                  <li key={plan.key} className="pricing-tiers__item">
                    <PricingPlanCard
                      plan={plan}
                      allFeatures={pricing.planFeatures ?? []}
                      currency={pricing.currency}
                      threeYearOfferEndsOn={pricing.threeYearOfferEndsOn ?? null}
                      signupAvailable={pricing.signupAvailable}
                    />
                  </li>
                ))}
              </ul>
            </section>
          );
        }
        // A sport with no plans is priced by school tier (the API lists only those sports in tiers).
        if (key === 'softball' && !withPlans.has(key) && pricing.tiers.length > 0) {
          return (
            <section key={key} className="pricing-sport" aria-labelledby="pricing-sport-softball" data-testid="pricing-softball">
              <h3 className="pricing-sport__title" id="pricing-sport-softball">
                Softball
              </h3>
              <p className="pricing-sport__lead">Your price is set by your school&apos;s level and conference.</p>
              <TierCards pricing={pricing} headingLevel={4} />
            </section>
          );
        }
        return null;
      })}
    </>
  );
};

const TierList = ({ pricing }: { pricing: PublicPricingResponse }) => {
  const trialDays = pricing.trialDays ?? null;
  const trial = trialLine(trialDays);
  return (
    <>
      {trial && (
        <p className="pricing-trial" data-testid="pricing-trial">
          {trial}
        </p>
      )}
      {!pricing.signupAvailable && (
        <p className="pricing-notice" data-testid="pricing-signup-closed">
          Online signup is closed for now. Talk to us and we will get your program set up.
        </p>
      )}
      {hasPlans(pricing) ? <SportSections pricing={pricing} /> : <TierCards pricing={pricing} />}
    </>
  );
};

/**
 * Loading, error, empty or the cards. Loading is a `status` and a load failure an `alert`, so each is
 * announced as it appears. The cards and the empty state are not live regions (a live list would read
 * every card aloud); after Try again, focus sits on the tier area wrapper, so the coach is already
 * there when they arrive.
 */
const TierArea = ({ state, onRetry }: { state: PublicPricingState; onRetry: () => void }) => {
  if (state.status === 'loading') {
    return (
      <p className="pricing-state" role="status" data-testid="pricing-loading">
        Loading prices...
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="pricing-state pricing-state--error" role="alert" data-testid="pricing-error">
        <p>{state.message}</p>
        <Button type="button" variant="secondary" className="mk-btn mk-btn--outline pricing-retry" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (state.pricing.tiers.length === 0 && !hasPlans(state.pricing)) {
    return (
      <div className="pricing-state" data-testid="pricing-empty">
        <h3 className="pricing-state__title">Pricing is coming soon</h3>
        <p>We are setting our prices for the season. Talk to us and we will tell you what your program would pay.</p>
        <PricingCta signupAvailable={false} />
      </div>
    );
  }

  return <TierList pricing={state.pricing} />;
};

/**
 * `/pricing`: the public price list, linked from rosterpilot.com.
 *
 * Public and outside every authenticated shell, like `/signup`, so it works signed out and signed
 * in alike. It wears rosterpilot.com's header, footer and styles rather than the app's
 * (`MarketingChrome`, the `--marketing-*` tokens in `index.css`), because a coach arrives here from
 * that site, which links straight to it, and should not notice the change of site.
 *
 * The page is its own shell, so it renders its own skip link and owns the `main#main-content`
 * landmark it targets.
 * No guided tour: tours run for signed-in coaches inside the app, and this page is pre-app.
 */
export const PricingPage = () => {
  const { state, retry } = usePublicPricing();
  // The closing band follows the same switch as the cards: signup only when it is open and there
  // is something to buy. While loading or after an error it still offers signup, because the
  // signup flow makes its own check and a wrong guess costs one screen, not a dead end.
  const offerSignup =
    state.status !== 'ready' ||
    (state.pricing.signupAvailable && (state.pricing.tiers.length > 0 || hasPlans(state.pricing)));
  // Until prices load the copy below says nothing about a trial, rather than guessing.
  const trialDays = state.status === 'ready' ? (state.pricing.trialDays ?? null) : null;
  // Baseball plans change the copy that says the school sets the price. Until prices load, the
  // copy is as it always was.
  const plans = state.status === 'ready' && hasPlans(state.pricing);
  // Softball keeps its school prices only while it has no plans on sale.
  const softballByPlan = state.status === 'ready' && sportsWithPlans(state.pricing).has('softball');
  const planLead = softballByPlan
    ? 'Built with coaches, in season. Pick the plan that fits your program.'
    : 'Built with coaches, in season. Baseball programs pick a plan. Softball prices go by school.';

  // Try again unmounts itself (the area switches to loading), which would drop keyboard focus to the
  // page. Focus the tier area first: it stays mounted through every state, so focus stays put.
  const tierAreaRef = useRef<HTMLDivElement>(null);
  const handleRetry = useCallback(() => {
    tierAreaRef.current?.focus();
    retry();
  }, [retry]);

  return (
    <div className="pricing-page">
      <a className="mk-skip" href="#main-content">
        Skip to main content
      </a>
      <MarketingHeader />
      <main id="main-content" tabIndex={-1} className="pricing-main">
        <section className="pricing-hero" aria-labelledby="pricing-title">
          <div className="mk-wrap pricing-hero__inner">
            <p className="mk-eyebrow">Pricing</p>
            <h1 className="pricing-hero__title" id="pricing-title">
              {plans ? (
                <>
                  One yearly price for <span className="pricing-hero__mark">your program</span>.
                </>
              ) : (
                <>
                  One yearly price, set by your school and <span className="pricing-hero__mark">your sport</span>.
                </>
              )}
            </h1>
            <p className="pricing-hero__lead" data-testid="pricing-hero-lead">
              {plans
                ? offerSignup
                  ? `${planLead} Sign up with your school email.`
                  : `${planLead} Talk to us and we will set up your program.`
                : offerSignup
                  ? 'Built with coaches, in season. Find your level below, then sign up with your school email.'
                  : 'Built with coaches, in season. Find your level below, then talk to us and we will set up your program.'}
            </p>
          </div>
        </section>

        <section className="pricing-section pricing-section--tiers" aria-labelledby="pricing-tiers-title">
          <div className="mk-wrap">
            <h2 className="pricing-visually-hidden" id="pricing-tiers-title">
              {plans ? 'Prices by sport' : 'Prices by tier'}
            </h2>
            <div
              ref={tierAreaRef}
              className="pricing-tier-area"
              role="group"
              aria-labelledby="pricing-tiers-title"
              tabIndex={-1}
              data-testid="pricing-tier-area"
            >
              <TierArea state={state} onRetry={handleRetry} />
            </div>
          </div>
        </section>

        <PricingHowItWorks signupAvailable={offerSignup} trialDays={trialDays} hasPlans={plans} />
        <PricingFaq signupAvailable={offerSignup} trialDays={trialDays} hasPlans={plans} />

        <section className="pricing-band" aria-labelledby="pricing-band-title">
          <div className="mk-wrap pricing-band__inner">
            <p className="mk-eyebrow">Transform your recruiting</p>
            <h2 className="pricing-band__title" id="pricing-band-title">
              No spreadsheets. No videos to text. No after-hours admin work.
            </h2>
            <p className="pricing-band__lead">
              Leave your next event with your recruiting work already done.
            </p>
            <div className="pricing-band__actions">
              <PricingCta signupAvailable={offerSignup} tone="gold" testId="pricing-band-cta" />
              {offerSignup && (
                <PricingCta signupAvailable={false} tone="ghost-dark" testId="pricing-band-talk" />
              )}
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
};
